// ============================================
// Analyst Bar — Pure Rendering Surface
//
// Receives AnalystBarProps and renders 5 states:
// IDLE | SYMPTOM | CAPTAIN | BIOMARKER | PROTOCOL
//
// Priority: CAPTAIN > PROTOCOL > BIOMARKER > SYMPTOM > IDLE
// ============================================

import { useRef, useEffect } from 'react';
import type {
  AnalystBarProps,
  BarMarker,
  BarSymptom,
  BarProtocol,
  JourneyStage,
} from '../../../types/analystBar';

// ── Design Tokens (bar-specific, black background) ──

const BAR_BG = '#000000';
const DIVIDER = '#333333';
const W = '#FFFFFF';
const GRAY = '#999999';
const DIM = '#555555';
const ROSE = '#E11D48';
const RED = '#EF4444';
const AMBER = '#D97706';
const GREEN = '#16A34A';
const R = 4;

type FlagKey = 'attention' | 'borderline' | 'optimal' | 'pending';

const FLAG_COLORS: Record<FlagKey, string> = {
  attention: RED,
  borderline: AMBER,
  optimal: GREEN,
  pending: '#444444',
};
const FLAG_BG: Record<FlagKey, string> = {
  attention: '#7F1D1D',
  borderline: '#7C2D12',
  optimal: '#14532D',
  pending: '#262626',
};
const FLAG_BORDER: Record<FlagKey, string> = {
  attention: RED,
  borderline: AMBER,
  optimal: GREEN,
  pending: '#555555',
};

const FONT_BODY = "'DM Sans',sans-serif";
const FONT_MONO = "'JetBrains Mono',monospace";

// ── Keyframe CSS (injected once) ──

const KEYFRAMES = `
@keyframes slideIn{from{opacity:0;transform:translateX(20px)}to{opacity:1;transform:translateX(0)}}
@keyframes pg{0%,100%{box-shadow:0 0 8px #E11D48}50%{box-shadow:0 0 16px #E11D48}}
@keyframes pr{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
@keyframes ct{from{max-width:0}to{max-width:600px}}
`;

// ── PillIcon SVG ──

function PillIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke={ROSE}
      strokeWidth="2.5"
      strokeLinecap="round"
    >
      <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" />
      <path d="m8.5 8.5 7 7" />
    </svg>
  );
}

// ── Sub-States ──

function IdleState() {
  return (
    <div
      style={{
        height: 38,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.14em',
        color: DIM,
        fontFamily: FONT_BODY,
      }}
    >
      <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#333' }} />
      LAB ANALYST
      <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#333' }} />
    </div>
  );
}

function SymptomRow({
  symptoms,
  variant,
}: {
  symptoms: BarSymptom[];
  variant: 'standalone' | 'sub';
}) {
  if (variant === 'standalone') {
    return (
      <div
        style={{
          height: 48,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 16px',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch' as never,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: GRAY,
            fontFamily: FONT_BODY,
            flexShrink: 0,
          }}
        >
          DETECTED
        </span>
        {symptoms.map((s, i) => (
          <div
            key={s.term}
            style={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: W,
              background: '#7C2D12',
              padding: '5px 14px',
              borderRadius: R,
              fontFamily: FONT_BODY,
              border: `1px solid ${AMBER}`,
              animation: `slideIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both`,
              animationDelay: `${i * 0.3}s`,
              flexShrink: 0,
            }}
          >
            {s.term}
          </div>
        ))}
      </div>
    );
  }

  // Sub-row variant (inside biomarker state)
  return (
    <div
      style={{
        display: 'flex',
        gap: 8,
        padding: '0 12px 8px',
        height: 34,
        alignItems: 'center',
        borderTop: `1px solid ${DIVIDER}`,
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch' as never,
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.1em',
          color: GRAY,
          fontFamily: FONT_BODY,
          flexShrink: 0,
        }}
      >
        DETECTED
      </span>
      {symptoms.map((s) => (
        <div
          key={s.term}
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: W,
            background: '#7C2D12',
            padding: '4px 14px',
            borderRadius: R,
            fontFamily: FONT_BODY,
            border: `1px solid ${AMBER}`,
            flexShrink: 0,
          }}
        >
          {s.term}
        </div>
      ))}
    </div>
  );
}

function CaptainState({
  message,
  journeyStage,
}: {
  message: string;
  journeyStage: JourneyStage;
}) {
  const instant = journeyStage !== 'first_visit';

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        background: BAR_BG,
        display: 'flex',
        alignItems: 'center',
        padding: '0 18px',
        gap: 12,
      }}
    >
      <div
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: ROSE,
          animation: 'pg 1.5s ease infinite',
          flexShrink: 0,
        }}
      />
      <div
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: W,
          fontFamily: FONT_MONO,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          animation: instant ? undefined : 'ct 1.8s steps(50) forwards',
          maxWidth: instant ? undefined : undefined,
        }}
      >
        {message}
      </div>
    </div>
  );
}

function BiomarkerState({
  markers,
  symptoms,
  scrollRef,
}: {
  markers: BarMarker[];
  symptoms: BarSymptom[];
  scrollRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <div
      style={{
        height: symptoms.length > 0 ? 88 : 56,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '0 12px',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch' as never,
          scrollbarWidth: 'none',
        }}
      >
        {markers.map((m) => (
          <div
            key={m.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 12px',
              borderRadius: R,
              background: FLAG_BG[m.flag],
              border: `1px solid ${FLAG_BORDER[m.flag]}`,
              flexShrink: 0,
              animation: 'slideIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both',
              transition: 'background 0.4s ease, border-color 0.4s ease',
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: FLAG_COLORS[m.flag],
                boxShadow:
                  m.flag !== 'pending'
                    ? `0 0 6px ${FLAG_COLORS[m.flag]}`
                    : 'none',
                transition: 'all 0.4s ease',
                flexShrink: 0,
              }}
            />
            <span
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: W,
                fontFamily: FONT_BODY,
              }}
            >
              {m.name}
            </span>
            <span
              style={{
                fontSize: 15,
                fontWeight: 700,
                color:
                  m.flag === 'pending' ? DIM : FLAG_COLORS[m.flag],
                fontFamily: FONT_MONO,
                transition: 'color 0.4s ease',
              }}
            >
              {m.value}
            </span>
          </div>
        ))}
      </div>
      {symptoms.length > 0 && (
        <SymptomRow symptoms={symptoms} variant="sub" />
      )}
    </div>
  );
}

function ProtocolState({ protocol }: { protocol: BarProtocol }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 4,
        background: BAR_BG,
        padding: '8px 16px',
        animation: 'pr 0.5s ease',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 6,
        }}
      >
        <PillIcon />
        <span
          style={{
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: ROSE,
            fontFamily: FONT_BODY,
          }}
        >
          {protocol.gateLabel}
        </span>
      </div>
      <div style={{ display: 'flex' }}>
        {protocol.products.map((p, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              padding: '0 10px',
              borderRight:
                i < protocol.products.length - 1
                  ? `1px solid ${DIVIDER}`
                  : 'none',
            }}
          >
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: W,
                fontFamily: FONT_BODY,
                lineHeight: 1.3,
              }}
            >
              {p.name}
            </div>
            <div
              style={{
                fontSize: 17,
                fontWeight: 700,
                color: '#4ADE80',
                fontFamily: FONT_MONO,
              }}
            >
              ${p.monthlyPrice}
              <span style={{ fontSize: 11, color: GRAY }}>/mo</span>
            </div>
          </div>
        ))}
        <div
          style={{
            padding: '0 4px 0 12px',
            textAlign: 'right',
            minWidth: 56,
            borderLeft: `1px solid ${DIVIDER}`,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: GRAY,
              letterSpacing: '0.06em',
              fontFamily: FONT_BODY,
            }}
          >
            DAILY
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: W,
              fontFamily: FONT_MONO,
            }}
          >
            ${protocol.dailyPrice.toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ──

export function AnalystBar({
  markers,
  symptoms,
  captainMsg,
  protocol,
  journeyStage,
}: AnalystBarProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll markers to end when new ones arrive
  useEffect(() => {
    if (scrollRef.current) {
      setTimeout(() => {
        scrollRef.current?.scrollTo({
          left: scrollRef.current.scrollWidth,
          behavior: 'smooth',
        });
      }, 100);
    }
  }, [markers.length]);

  // Derive state (priority: CAPTAIN > PROTOCOL > BIOMARKER > SYMPTOM > IDLE)
  const hasMarkers = markers.length > 0;
  const hasSymptoms = symptoms.length > 0;
  const showProtocol = !!protocol && !captainMsg;
  const showBiomarker = hasMarkers && !captainMsg && !showProtocol;
  const showSymptomOnly = hasSymptoms && !hasMarkers && !showProtocol && !captainMsg;
  const idle = !showSymptomOnly && !hasMarkers && !captainMsg && !showProtocol;

  // Compute height
  let h = 38;
  if (showSymptomOnly) h = 48;
  if (hasMarkers && !hasSymptoms && !showProtocol) h = 56;
  if (hasMarkers && hasSymptoms && !showProtocol) h = 88;
  if (showProtocol) h = 88;
  if (captainMsg) h = 56;

  return (
    <>
      <style>{KEYFRAMES}</style>
      <div
        style={{
          background: BAR_BG,
          overflow: 'hidden',
          position: 'relative',
          borderBottom: `1px solid ${DIVIDER}`,
          transition: 'height 0.5s cubic-bezier(0.4,0,0.2,1)',
          height: h,
        }}
      >
        {idle && <IdleState />}

        {showSymptomOnly && (
          <SymptomRow symptoms={symptoms} variant="standalone" />
        )}

        {captainMsg && (
          <CaptainState message={captainMsg} journeyStage={journeyStage} />
        )}

        {showProtocol && protocol && <ProtocolState protocol={protocol} />}

        {showBiomarker && (
          <BiomarkerState
            markers={markers}
            symptoms={symptoms}
            scrollRef={scrollRef}
          />
        )}
      </div>
    </>
  );
}
